import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Formik, Field, Form } from "formik";
import tw from "tailwind-styled-components";
import { MdClose, MdEdit } from "react-icons/md";
import {
    ModalHeader,
    ActionSection,
    SideCancelBtn,
    SubmitBtn,
    InputGroup,
    FieldWrappers,
    Label,
    Wrapper,
    ScrollableContent,
} from "../../Styles/InputStyles";
import MultiPropSelectModal from "../../MultiPropSelectModal";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const EditKitchen = ({
    handleClose,
    editKitchenMutate,
    editKitchenLoading = false,
    selectedData
}) => {

    const [height, setHeight] = useState(window.innerHeight);
    const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);

    // Get properties from Redux or prop
    // const properties = useSelector((state) => state.PropertyReducer.property) || [];
    const properties =
        (useSelector((state) => state.PropertyReducer.property) || [])
            .filter((item) => item.id !== 0);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Get initial values from selectedData
    const getInitialValues = () => {
        if (!selectedData) {
            return {
                id: "",
                name: "",
                propertyIds: [],
                propertyDisplay: "",
            };
        }

        // Extract property IDs from linkedProperties
        const propertyIds = selectedData.linkedProperties?.map(prop => prop.id) || [];

        return {
            id: selectedData.id || "",
            name: selectedData.name || "",
            propertyIds: propertyIds,
            propertyDisplay: "",
        };
    };

    const validate = (values) => {
        const errors = {};

        if (!values.name || values.name.trim() === "") {
            errors.name = "Kitchen name is required";
        } else if (values.name.length < 2) {
            errors.name = "Kitchen name must be at least 2 characters";
        } else if (values.name.length > 50) {
            errors.name = "Kitchen name must be less than 50 characters";
        }

        if (!values.propertyIds || values.propertyIds.length === 0) {
            errors.propertyIds = "Please select at least one property";
        }

        return errors;
    };

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        // Prepare payload
        const payload = {
            kitchenId: parseInt(selectedData.id),
            name: values.name.trim(),
            propIds: values.propertyIds.map(id => parseInt(id)),
        };

        // Call the mutation
        editKitchenMutate(payload);
    };

    const handlePropertySelect = (selectedIds, setFieldValue) => {
        setFieldValue("propertyIds", selectedIds);

        // Update display text
        const selectedNames = properties
            .filter(prop => selectedIds.includes(prop.id))
            .map(prop => prop.name);

        if (selectedNames.length === 0) {
            setFieldValue("propertyDisplay", "Select Properties");
        } else if (selectedNames.length <= 2) {
            setFieldValue("propertyDisplay", selectedNames.join(", "));
        } else {
            setFieldValue("propertyDisplay", `${selectedNames.slice(0, 2).join(", ")} +${selectedNames.length - 2} more`);
        }

        setIsPropertyModalOpen(false);
    };

    // Get property names for display
    const getPropertyDisplay = useCallback((propertyIds) => {
        if (!propertyIds || propertyIds.length === 0) return "Select Properties";

        const selectedNames = properties
            .filter(prop => propertyIds.includes(prop.id))
            .map(prop => prop.name);

        if (selectedNames.length === 0) return "Select Properties";
        if (selectedNames.length <= 2) return selectedNames.join(", ");
        return `${selectedNames.slice(0, 2).join(", ")} +${selectedNames.length - 2} more`;
    }, [properties]);

    // Get initial values
    const initialValues = getInitialValues();

    return (
        <Container style={{ height }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Edit Kitchen</h1>
                <button onClick={handleClose} type="button">
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validate={validate}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue, errors, touched, isSubmitting }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100vh-120px)]">
                                <Wrapper>
                                    {/* Kitchen Name */}
                                    <InputGroup>
                                        <Label htmlFor="name">Kitchen Name *</Label>
                                        <FieldWrappers>
                                            <Field
                                                type="text"
                                                name="name"
                                                id="name"
                                                placeholder="Enter kitchen name (e.g., Main Kitchen)"
                                                className="w-full"
                                            />
                                        </FieldWrappers>
                                        {errors.name && touched.name && (
                                            <ErrorText>{errors.name}</ErrorText>
                                        )}
                                    </InputGroup>

                                    {/* Property Selection */}
                                    <InputGroup>
                                        <Label>Select Properties *</Label>
                                        <FieldWrappers
                                            onClick={() => setIsPropertyModalOpen(true)}
                                            className="cursor-pointer"
                                        >
                                            <Field
                                                type="text"
                                                name="propertyDisplay"
                                                readOnly
                                                value={getPropertyDisplay(values.propertyIds)}
                                                className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                placeholder="Select Properties"
                                            />
                                            <div className="flex items-center gap-2 flex-shrink-0 mr-2">
                                                {values.propertyIds && values.propertyIds.length > 0 && (
                                                    <SelectedCountBadge>
                                                        {values.propertyIds.length}
                                                    </SelectedCountBadge>
                                                )}
                                                <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200" />
                                            </div>
                                        </FieldWrappers>
                                        {errors.propertyIds && touched.propertyIds && (
                                            <ErrorText>{errors.propertyIds}</ErrorText>
                                        )}
                                    </InputGroup>

                                    {/* Selected Properties Preview */}
                                    {values.propertyIds && values.propertyIds.length > 0 && (
                                        <SelectedPropertiesPreview>
                                            <p className="text-xs font-medium text-gray-500 mb-2">
                                                Selected Properties ({values.propertyIds.length}):
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {properties
                                                    .filter(prop => values.propertyIds.includes(prop.id))
                                                    .map(prop => (
                                                        <PropertyTag key={prop.id}>
                                                            {prop.name}
                                                        </PropertyTag>
                                                    ))}
                                            </div>
                                        </SelectedPropertiesPreview>
                                    )}

                                </Wrapper>
                            </ScrollableContent>

                            <ActionSection>
                                <SideCancelBtn type="button" onClick={handleClose}>
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn
                                    type="submit"
                                    disabled={isSubmitting || editKitchenLoading}
                                >
                                    {isSubmitting || editKitchenLoading ? "Updating..." : "Update"}
                                </SubmitBtn>
                            </ActionSection>

                            {/* Multi-Property Selection Modal */}
                            <MultiPropSelectModal
                                isOpen={isPropertyModalOpen}
                                onClose={() => setIsPropertyModalOpen(false)}
                                options={properties}
                                selectedProperties={values.propertyIds || []}
                                onSelect={(selectedIds) => handlePropertySelect(selectedIds, setFieldValue)}
                                isLoading={false}
                            />
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5 mt-1`;
const SelectedCountBadge = tw.span`
    inline-flex items-center justify-center
    w-5 h-5
    bg-primary-color text-white
    rounded-full text-xs font-medium
`;
const SelectedPropertiesPreview = tw.div`
    mt-2 p-3
    bg-gray-50
    border border-gray-200
    rounded-lg
`;
const PropertyTag = tw.span`
    inline-flex items-center
    px-2 py-1
    bg-primary-color/10 text-primary-color
    rounded-full text-xs font-medium
    border border-primary-color/20
`;
const InfoBox = tw.div`
    mt-4 p-4 
    bg-primary-color/10 text-primary-color
    border border-primary-color/40
    rounded-lg
`;
const InfoTitle = tw.h4`
    text-sm font-medium text-primary-color 
    mb-1
`;
const InfoText = tw.p`
    text-sm text-primary-color
`;
export default EditKitchen;